import React, { useLayoutEffect, useRef } from 'react';
import type { Node } from '@glueapp/graph-types';
import { GetVariable, SetVariable } from '@glueapp/graph-nodes';
import { usePortPositions } from '@/GraphEditor/drawingLayer/contexts/PortPositionsContext';
import { usePortOverlayNode } from '@/GraphEditor/drawingLayer/contexts/PortOverlayNodeContext';

interface Props {
    node: Node;
}

export const SpecialNodeMarker: React.FC<Props> = ({ node }) => {
    const { openOverlay } = usePortOverlayNode();
    const markerRef = useRef<HTMLSpanElement>(null);
    const { setInPortPosition } = usePortPositions();

    const marker = (() => {
        if (node.id === GetVariable.id || node.id === SetVariable.id) {
            return { label: 'Variable', portName: '__variable__' as const };
        }
        if (node.triggerType === 'Timer') {
            return { label: 'Schedule', portName: '__schedule__' as const };
        }
        if (node.triggerType === 'Webhook') {
            return { label: 'Edit Webhook', portName: '__webhook__' as const };
        }
        return null;
    })();

    useLayoutEffect(() => {
        if (!marker) return;
        const el = markerRef.current;
        if (el) {
            const nodeContainer = el.closest('[data-node-container]') as HTMLElement | null;
            const rect = el.getBoundingClientRect();
            setInPortPosition(marker.portName, {
                x: nodeContainer ? nodeContainer.getBoundingClientRect().left : rect.left,
                y: rect.y + rect.height / 2,
            });
        }
    }, [marker, setInPortPosition]);

    const handlePointerDown = (e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (marker) {
            openOverlay(marker.portName, 'special');
        }
    };

    if (!marker) return null;

    return (
        <span
            ref={markerRef}
            onPointerDown={handlePointerDown}
            className="inline-flex items-center cursor-pointer hover:underline"
            style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 6, paddingBottom: 6 }}
        >
            {marker.label}
        </span>
    );
};
